import { Icon } from "@qw/ui";
import { BookRow } from "./book-row";
import { BOOK_ROW_GUTTER_CLASSNAME } from "./book-row-gutter";
import { UNNAMED_SCOPE, useBookScope } from "./use-book-scope";

/** An agent is a person; an agency is the set of them the rail is filtered to. */
const SCOPE_ICON = { agency: "groups", agent: "person" } as const;

/** What the rail is short *of*, in the words the broker would use for it. */
const SCOPE_NOUN = { agency: "agency", agent: "agent" } as const;

/**
 * Why the Book is short.
 *
 * A set scope filters the rail, and **the median one holds exactly one open account** — 69.2% of
 * agent scopes hold exactly one, 56.3% of agency scopes do. A column that silently drops from a
 * broker's whole book to a single row reads as a failed load, not as a filter, so the filter says so
 * in the rail rather than only in the search box two panels up.
 *
 * Deliberately not a control. The chip in the search box is the way out, and a second one here
 * would be a second thing to keep in step with it — `useClearScopeTarget` exists because the chip
 * and the Backspace handler already had that problem. This says what happened; the chip undoes it.
 *
 * Above `ColumnContent` for the same reason `SelectedInsuredRow` is: scrolling the Book must not
 * take the explanation with it. It borrows that row's gutter so the text lines up with the insured
 * names below it, with the scope's own icon where a book row carries its state icon.
 */
export const BookScopeRow = () => {
  const bookScope = useBookScope();

  if (!bookScope) {
    return null;
  }

  const { dimension, name, insuredCount } = bookScope;
  // No account inside the scope means no name for it either — the name is read off those accounts.
  // So the empty case cannot name the scope, and saying "0 accounts" next to a blank would read
  // worse than saying the one thing that is actually known.
  const label =
    insuredCount === 0
      ? `No accounts in your book for this ${SCOPE_NOUN[dimension]}`
      : `${name ?? UNNAMED_SCOPE[dimension]} · ${insuredCount} ${insuredCount === 1 ? "account" : "accounts"}`;

  return (
    <BookRow className="mx-1 mb-1 grid-cols-[1fr]">
      <div className="grid grid-cols-[--spacing(18)_1fr] items-center gap-3 pl-1 text-left text-xs text-muted-foreground">
        <span className={BOOK_ROW_GUTTER_CLASSNAME}>
          <Icon icon={SCOPE_ICON[dimension]} />
        </span>
        <span className="truncate">{label}</span>
      </div>
    </BookRow>
  );
};
